'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
  getPrimary,
  getSubCongregations,
  type LocationCongregation,
  type LocationGroup,
} from '@/lib/types';
import { CampusIcon, WoodsideLogo } from './CampusIcon';

interface CampusDropdownProps {
  locations: LocationGroup[];
  /** null = all campuses */
  selectedId: number | null;
  onSelect: (cong: LocationCongregation | null, svgUrl: string | null) => void;
  loading?: boolean;
}

/**
 * The campus selector, a port of the campus-picker widget's dropdown with one
 * extra row: "All campuses" under the Woodside mark. Picking a campus here
 * behaves like the site header's picker (cookie + event + MP home campus);
 * "All campuses" only widens this list.
 */
export function CampusDropdown({
  locations,
  selectedId,
  onSelect,
  loading = false,
}: CampusDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click (composedPath so it works from inside the shadow root)
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.composedPath()[0] as Node;
      if (containerRef.current && !containerRef.current.contains(target)) setOpen(false);
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Keep the panel on screen
  useEffect(() => {
    if (!open || !panelRef.current || !containerRef.current) return;
    const panel = panelRef.current;
    const rect = containerRef.current.getBoundingClientRect();
    panel.style.left = '0px';
    panel.style.right = 'auto';
    const panelRect = panel.getBoundingClientRect();
    if (panelRect.right > window.innerWidth - 8) {
      panel.style.left = 'auto';
      panel.style.right = '0px';
    }
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < panelRect.height + 8 && rect.top > spaceBelow) {
      panel.style.top = 'auto';
      panel.style.bottom = '100%';
      panel.style.marginTop = '0';
      panel.style.marginBottom = '6px';
    }
  }, [open]);

  const { selected, selectedGroup, otherGroups } = useMemo(() => {
    if (selectedId === null) return { selected: null, selectedGroup: null, otherGroups: locations };
    const group = locations.find((g) => g.congregations.some((c) => c.id === selectedId)) ?? null;
    const cong = group?.congregations.find((c) => c.id === selectedId) ?? null;
    return {
      selected: cong,
      selectedGroup: group,
      otherGroups: locations.filter((g) => g !== group),
    };
  }, [locations, selectedId]);

  const choose = useCallback(
    (cong: LocationCongregation | null, svgUrl: string | null) => {
      setOpen(false);
      onSelect(cong, svgUrl);
    },
    [onSelect]
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex w-full items-center gap-2.5 rounded-full border border-gray-200 bg-white py-2.5 pr-3 pl-3 text-sm font-semibold text-[#1c2b39] shadow-sm transition-all hover:shadow-md active:scale-[0.98] sm:w-auto sm:min-w-[210px]"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center">
          {selected ? (
            <CampusIcon congregationId={selected.id} className="h-7 w-7" />
          ) : (
            <WoodsideLogo className="h-6 w-6" />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-left">
          {loading ? 'Loading campuses' : selected ? selected.display_name : 'All campuses'}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && locations.length > 0 && (
        <div
          ref={panelRef}
          role="listbox"
          className="scrollbar-hide absolute top-full z-[60] mt-1.5 max-h-[420px] w-full overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-xl sm:w-[320px]"
        >
          <div className="p-3 pb-1">
            <p className="mb-2 text-[10px] font-bold tracking-widest text-gray-400 uppercase">
              Showing
            </p>
            <Row
              label="All campuses"
              sub="Every event across Woodside"
              icon={<WoodsideLogo className="h-7 w-7" />}
              selected={selectedId === null}
              onClick={() => choose(null, null)}
            />
          </div>

          {selected && selectedGroup && (
            <>
              <hr className="mx-3 border-gray-200" />
              <div className="p-3 pb-1">
                <p className="mb-2 text-[10px] font-bold tracking-widest text-gray-400 uppercase">
                  Selected campus
                </p>
                <Group group={selectedGroup} selectedId={selectedId} onSelect={choose} />
              </div>
            </>
          )}

          {otherGroups.length > 0 && (
            <>
              <hr className="mx-3 border-gray-200" />
              <div className="p-3 pt-2">
                <p className="mb-2 text-[10px] font-bold tracking-widest text-gray-400 uppercase">
                  {selected ? 'All campuses' : 'Pick a campus'}
                </p>
                {otherGroups.map((group, i) => (
                  <div key={group.id}>
                    {i > 0 && <hr className="mx-2 my-1.5 border-gray-200" />}
                    <Group group={group} selectedId={selectedId} onSelect={choose} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Group({
  group,
  selectedId,
  onSelect,
}: {
  group: LocationGroup;
  selectedId: number | null;
  onSelect: (cong: LocationCongregation, svgUrl: string | null) => void;
}) {
  const primary = getPrimary(group);
  const subs = getSubCongregations(group);
  return (
    <div className="mb-1">
      <Row
        label={primary.display_name}
        icon={<CampusIcon congregationId={primary.id} className="h-8 w-8" />}
        selected={primary.id === selectedId}
        onClick={() => onSelect(primary, group.svg_url)}
      />
      {subs.map((cong) => (
        <Row
          key={cong.id}
          label={cong.display_name}
          selected={cong.id === selectedId}
          onClick={() => onSelect(cong, group.svg_url)}
          nested
        />
      ))}
    </div>
  );
}

function Row({
  label,
  sub,
  icon,
  selected,
  onClick,
  nested = false,
}: {
  label: string;
  sub?: string;
  icon?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  nested?: boolean;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={`flex w-full cursor-pointer items-center gap-3 rounded-xl text-left transition-colors ${
        nested ? 'py-2 pr-2 pl-[52px]' : 'px-2 py-2.5'
      } ${selected ? 'bg-[#299c8f]/10' : 'hover:bg-gray-50'}`}
    >
      {!nested && <span className="flex h-8 w-8 shrink-0 items-center justify-center">{icon}</span>}
      <span className="min-w-0 flex-1">
        <span
          className={`block leading-tight font-semibold ${nested ? 'text-[13px]' : 'text-sm'} ${
            selected ? 'text-[#299c8f]' : 'text-[#1c2b39]'
          }`}
        >
          {label}
        </span>
        {sub && (
          <span className="mt-0.5 block truncate text-[11px] leading-tight text-gray-400">
            {sub}
          </span>
        )}
      </span>
      {selected && <Check className="h-4 w-4 shrink-0 text-[#299c8f]" />}
    </button>
  );
}
